/**
 * Membuka envelope sukses HBS.
 *
 * HBS membungkus SETIAP respons sukses menjadi `{ data: <payload> }`
 * (`ResponseEnvelopeInterceptor`, dipasang global di `main.ts`; hanya rute ber-
 * `@SkipResponseEnvelope()` seperti `/health` yang dikecualikan). Rute undangan tidak termasuk
 * pengecualian itu — diperiksa langsung terhadap produksi 21 Agu 2026: `GET .../invitations`
 * menjawab dengan SATU kunci di lapisan atas, yaitu `data`.
 *
 * Karena itu `payload.token` selalu `undefined`, dan pemanggilnya menyimpulkan "HBS tidak
 * mengembalikan token" padahal tokennya ada — hanya satu lapis lebih dalam.
 *
 * Kedua bentuk sengaja diterima. Layar masuk sudah lebih dulu melakukan hal yang sama untuk
 * `/me/memberships`, dan alasannya berlaku di sini juga: klien ini bicara dengan API yang
 * envelope-nya dapat dilewati per-rute, jadi menuntut satu bentuk saja membuat klien pecah
 * karena keputusan yang tidak pernah ia lihat.
 */
export function unwrapEnvelope(payload: unknown): unknown {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  const rec = payload as Record<string, unknown>;
  return 'data' in rec ? rec.data : payload;
}

export interface CreatedInvitationShape {
  readonly id: string;
  readonly email: string;
  readonly token: string;
}

/**
 * Membaca hasil `POST /organizations/:id/invitations`.
 *
 * Bentuk yang dikirim HBS adalah `{ data: { invitation: {...}, token } }` — perhatikan `id` dan
 * `email` ada di dalam `invitation`, BUKAN bersebelahan dengan `token`. Pemanggil lama membaca
 * ketiganya di lapisan atas, jadi seandainya token pun ketemu, `id` dan `email` tetap kosong.
 *
 * `null` berarti tidak ada token yang dapat dipakai. Itu dibedakan dari "gagal": undangannya
 * SUDAH terkirim oleh server, dan yang hilang hanyalah kemampuan membangun tautan salin-tempel.
 */
export function parseCreatedInvitation(
  payload: unknown,
  emailDiminta: string,
): CreatedInvitationShape | null {
  const isi = unwrapEnvelope(payload);
  if (isi === null || typeof isi !== 'object') return null;

  const rec = isi as { token?: unknown; invitation?: unknown };
  if (typeof rec.token !== 'string' || rec.token.length === 0) return null;

  // `invitation` bila ada; kalau tidak, jatuh kembali ke objek yang sama — bentuk datar juga
  // pernah dipakai, dan membedakannya di sini lebih murah daripada di tiap pemanggil.
  const sumber = (
    rec.invitation !== null && typeof rec.invitation === 'object' ? rec.invitation : isi
  ) as { id?: unknown; email?: unknown };

  return {
    id: typeof sumber.id === 'string' ? sumber.id : '',
    email: typeof sumber.email === 'string' ? sumber.email : emailDiminta,
    token: rec.token,
  };
}

export interface RosterEntryShape {
  readonly id: string;
  readonly displayName: string;
}

/**
 * Membaca roster anggota dari `GET /organizations/:id/members`.
 *
 * ADA KARENA sapaan beranda berbunyi "Halo" tanpa nama untuk SEMUA orang (22 Agu 2026) — dan
 * penyebabnya cacat yang SAMA dengan `parseCreatedInvitation`, di fungsi tetangga pada berkas
 * yang sama. Pembacaan lama menanyakan `Array.isArray(data)` pada badan yang sebenarnya
 * `{ data: [...] }`; jawabannya selalu `false`, dan fungsi mengembalikan array KOSONG.
 *
 * Kegagalannya senyap TIGA lapis sekaligus: server menjawab 200, fungsinya tidak melempar, dan
 * pemanggilnya membungkus dengan `.catch(() => {})`. Yang tersisa di layar hanya sapaan tanpa
 * nama — yang juga merupakan tampilan SAH bagi orang yang memang belum mengisi namanya. Dua
 * sebab yang berjauhan, satu wajah yang sama persis.
 *
 * Baris tanpa `id` DIBUANG, bukan diberi `id: ''`. String kosong akan cocok dengan `userId` yang
 * juga kosong, lalu menyapa seseorang dengan nama orang lain — jauh lebih buruk daripada tidak
 * menyapa sama sekali.
 */
export function parseOrgRoster(payload: unknown): readonly RosterEntryShape[] {
  const isi = unwrapEnvelope(payload);
  if (!Array.isArray(isi)) return [];

  const hasil: RosterEntryShape[] = [];
  for (const baris of isi) {
    if (baris === null || typeof baris !== 'object') continue;
    const rec = baris as { id?: unknown; displayName?: unknown };
    if (typeof rec.id !== 'string' || rec.id.length === 0) continue;
    hasil.push({
      id: rec.id,
      displayName: typeof rec.displayName === 'string' ? rec.displayName : '',
    });
  }
  return hasil;
}

/**
 * Membaca hasil `POST /invitations/accept`.
 *
 * Cacat envelope yang sama, tetapi akibatnya SENYAP TOTAL: tanpa membuka `{ data }`, `userId`
 * selalu `undefined` dan pemanggil menerima string kosong tanpa satu pun tanda bahwa sesuatu
 * meleset — tidak ada layar yang memerah, tidak ada yang tercatat di mana pun.
 */
export function parseAcceptedInvitation(payload: unknown): { readonly userId: string } {
  const isi = unwrapEnvelope(payload);
  if (isi === null || typeof isi !== 'object') return { userId: '' };
  const rec = isi as { userId?: unknown };
  return { userId: typeof rec.userId === 'string' ? rec.userId : '' };
}

/**
 * Membaca hasil `POST /onboarding` (HBS) — Trial 14 hari tersendiri Workforce (BLD-015 lanjutan,
 * 2 Okt 2026). Dipanggil LANGSUNG dari sini (lintas-origin) — endpoint ini SUDAH ADA & SUDAH
 * berjalan untuk pendaftaran mandiri CRM sejak 13 Sep 2026 (ADR-0045 §3C D2), halaman ini cuma
 * pemanggil baru. BUKAN Demo Instan (dihapus di PR terpisah, #1452) — organisasi `customer`
 * sungguhan, bukan `trial_demo`.
 *
 * `null` berarti bentuknya tidak dikenali — dibedakan dari "berhasil tapi kosong" supaya
 * pemanggil tidak mengira organisasi sudah dibuat padahal `organizationId`-nya tak terpakai.
 */
export function parseOnboardingResult(
  payload: unknown,
): { readonly organizationId: string } | null {
  const isi = unwrapEnvelope(payload);
  if (isi === null || typeof isi !== 'object') return null;
  const rec = isi as { organizationId?: unknown };
  if (typeof rec.organizationId !== 'string' || rec.organizationId.length === 0) return null;
  return { organizationId: rec.organizationId };
}
